/**
 * useMediaBackup — EO «Бэкап медиа» (perm media_backup). Манифест всех медиа +
 * данные для поимённой выгрузки и экспорта manifest.json клиентом (Blob).
 *
 * Backend:
 *  - GET /api/eo/media/manifest      → { manifest, generatedAt, count, assets:[A] }
 *  - GET /api/eo/media/:id/download  → стрим файла (Content-Disposition: attachment)
 *
 * Скачивание manifest.json и отдельных файлов делает компонент через <a download>
 * / Blob — хук только грузит сам манифест.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

/** Один ассет в манифесте бэкапа (каноническое имя + контроль целостности). */
export interface BackupAsset {
  mediaId: string;
  fileName: string;
  ownerStaffId: string;
  kind: "photo" | "video" | string;
  telegramFileKey: string;
  thumbKey: string | null;
  sha256: string;
  bytes: number;
  origName: string | null;
  width: number | null;
  height: number | null;
  label: string | null;
  fanOnly: boolean;
  usedIn: string[];
  crops: Record<string, { x: number; y: number; w: number; h: number }> | null;
  filter: Record<string, unknown> | null;
  uploadedAt: string | null;
  downloadUrl: string;
}

export interface MediaManifest {
  manifest: string;
  generatedAt: string;
  count: number;
  assets: BackupAsset[];
}

export interface UseMediaBackupResult {
  manifest: MediaManifest | null;
  assets: BackupAsset[];
  count: number;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : "Ошибка";
}

export function useMediaBackup(): UseMediaBackupResult {
  const [manifest, setManifest] = useState<MediaManifest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api<MediaManifest>("/api/eo/media/manifest", { signal: ac.signal });
        if (ac.signal.aborted) return;
        setManifest(r);
        setLoading(false);
      } catch (e: unknown) {
        if (ac.signal.aborted) return;
        setManifest(null);
        setError(errMsg(e));
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [trigger]);

  return {
    manifest,
    assets: manifest?.assets ?? [],
    count: manifest?.count ?? manifest?.assets.length ?? 0,
    loading,
    error,
    reload,
  };
}
